/**
 * Rebalance questionnaire session — intro, answers, cursor, and the one submit.
 *
 * Belongs here: answers, the question cursor, feature leave by sticky `source`, and submit `viewPlanCta`.
 * The profile shown on the result screen is only the `riskProfile` query param.
 * Does not belong here: intro, header, or question markup.
 * Called from RebalanceQuestionnaireProvider. Retake calls resetSession.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { goBack, goToPortfolioDashboard } from '../../../../utils/appBridge'
import { useLoading } from '../../../../state-management/contexts/LoadingContext'
import { useToast } from '../../../../state-management/contexts/ToastContext'
import { SlideDirection } from '../../expert-review/shared/transitions/DirectionalSlideSwap'
import {
  findNextVisibleQuestionIndex,
  getVisibleQuestionsFromList
} from '../../expert-review/question-utils/visibleQuestions'
import type { Question, StrategyQuestionnaireAnswersMap } from '../../expert-review/shared/types/questionnaire.types'
import { mapRebalanceQuestions, useRebalanceQuestionnaireQuery } from '../api/useRebalanceQuestionnaireQuery'
import {
  buildRebalanceQuestionInputs,
  useSubmitRebalanceQuestionnaireMutation
} from '../api/useSubmitRebalanceQuestionnaireMutation'
import { RebalanceQuestionnaireSource, rebalanceRiskProfilePath } from '../rebalanceQuestionnairePaths'
import { parseRebalanceRiskProfile } from '../rebalanceRiskProfile'

const SUBMIT_FAILED_TOAST = 'Submitting failed.'

/** Session for every rebalance questionnaire route. The provider is the only caller. */
export function useRebalanceQuestionnaireFlow(entrySource: RebalanceQuestionnaireSource | null) {
  const navigate = useNavigate()
  const { showLoading, hideLoading } = useLoading()
  const { showToast } = useToast()
  const questionnaireQuery = useRebalanceQuestionnaireQuery()
  const submitMutation = useSubmitRebalanceQuestionnaireMutation()
  const [showIntro, setShowIntro] = useState(true)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<StrategyQuestionnaireAnswersMap>({})
  const [slideDirection, setSlideDirection] = useState(SlideDirection.Forward)
  const [viewPlanCta, setViewPlanCta] = useState<Record<string, unknown> | null>(null)

  const orderedQuestions = mapRebalanceQuestions(questionnaireQuery.data?.questions)
  const visibleQuestions = getVisibleQuestionsFromList(orderedQuestions, answers)
  const currentQuestion = visibleQuestions[questionIndex]
  const progressFill = visibleQuestions.length === 0 ? 0 : (questionIndex + 1) / visibleQuestions.length
  const hasQuestionnaire = questionnaireQuery.data != null && orderedQuestions.length > 0

  function resetSession() {
    setShowIntro(true)
    setQuestionIndex(0)
    setAnswers({})
    setSlideDirection(SlideDirection.Forward)
    setViewPlanCta(null)
  }

  /** Leave the feature — elite / rebalance plan → goBack; portfolio / missing → portfolio dashboard. */
  function exitRebalanceQuestionnaire() {
    console.log('exitRebalanceQuestionnaire', entrySource)
    if (
      entrySource === RebalanceQuestionnaireSource.ELITE_LANDING_DASHBOARD ||
      entrySource === RebalanceQuestionnaireSource.REBALANCE_PLAN_PAGE
    ) {
      goBack()
      return
    }
    goToPortfolioDashboard()
  }

  function handleIntroBack() {
    exitRebalanceQuestionnaire()
  }

  function dismissIntro() {
    setQuestionIndex(0)
    setShowIntro(false)
  }

  function handleQuestionBack() {
    if (questionIndex <= 0) {
      setShowIntro(true)
      return
    }
    setSlideDirection(SlideDirection.Back)
    setQuestionIndex(questionIndex - 1)
  }

  function updateDraftAnswer(questionId: string, value: string | number) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  function clearAnswer(questionId: string) {
    setAnswers((prev) => {
      const next = { ...prev }
      delete next[questionId]
      return next
    })
  }

  async function submitAnswers(nextAnswers: StrategyQuestionnaireAnswersMap, visible: Question[]) {
    const questionnaireId = questionnaireQuery.data?.questionnaireId ?? ''
    const questionInputs = buildRebalanceQuestionInputs(visible, nextAnswers)
    if (!questionnaireId || questionInputs.length === 0) {
      showToast({ message: SUBMIT_FAILED_TOAST, duration: 2500 })
      return
    }
    showLoading()
    try {
      const submitResponse = await submitMutation.mutateAsync({ questionnaireId, questionInputs })
      const parsedRiskProfile = parseRebalanceRiskProfile(submitResponse?.riskProfile ?? null)
      if (parsedRiskProfile == null) {
        showToast({ message: SUBMIT_FAILED_TOAST, duration: 2500 })
        return
      }
      setViewPlanCta(asViewPlanCta(submitResponse?.viewPlanCta))
      navigate(rebalanceRiskProfilePath(parsedRiskProfile), { replace: true })
    } catch {
      showToast({ message: SUBMIT_FAILED_TOAST, duration: 2500 })
    } finally {
      hideLoading()
    }
  }

  function handleAdvance(questionId: string, value: string | number) {
    const nextAnswers = { ...answers, [questionId]: value }
    const visibleAfter = getVisibleQuestionsFromList(orderedQuestions, nextAnswers)
    const next = findNextVisibleQuestionIndex(visibleQuestions, visibleAfter, questionId, questionIndex)
    setAnswers(nextAnswers)
    if (next == null) {
      void submitAnswers(nextAnswers, visibleAfter)
      return
    }
    setSlideDirection(SlideDirection.Forward)
    setQuestionIndex(next.nextIndex)
  }

  return {
    isLoading: questionnaireQuery.isLoading,
    isError: questionnaireQuery.isError,
    hasQuestionnaire,
    questionnaireId: questionnaireQuery.data?.questionnaireId ?? '',
    questions: orderedQuestions,
    showIntro,
    currentQuestion,
    answers,
    slideDirection,
    progressFill,
    viewPlanCta,
    resetSession,
    exitRebalanceQuestionnaire,
    handleIntroBack,
    dismissIntro,
    handleQuestionBack,
    handleAdvance,
    updateDraftAnswer,
    clearAnswer
  }
}

function asViewPlanCta(cta: unknown): Record<string, unknown> | null {
  if (cta == null || typeof cta !== 'object' || Array.isArray(cta)) return null
  return cta as Record<string, unknown>
}
